/**
 * Admin panel — visible only to users with is_admin=true.
 *
 * Two tabs:
 *   - Signup Requests: pending applications from /api/admin/signup-requests.
 *     Approve fires POST .../{id}/approve which creates the user + emails
 *     them a password-set link. Reject fires .../{id}/reject with an
 *     optional note.
 *   - Users: read-only list from /api/admin/users.
 *
 * Plus a quick "Open Mailbox" button that opens Hostinger webmail in a new
 * tab (the same mailbox the notification emails land in).
 */

import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useAuth } from 'auth/AuthContext';
import { api, ApiError } from 'api/client';

import userIcon from 'assets/windowsIcons/user.png';
import mailIcon from 'assets/windowsIcons/887(16x16).png';

const WEBMAIL_URL = 'https://mail.hostinger.com';

const TAB_REQUESTS = 'requests';
const TAB_USERS = 'users';

function formatTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts * 1000);
    return d.toLocaleString();
  } catch (e) {
    return '';
  }
}

function errorMessage(err) {
  if (err instanceof ApiError) {
    return err.message || err.code || 'Something went wrong.';
  }
  return (err && err.message) || 'Something went wrong.';
}

export default function Admin({ onClose }) {
  const { user } = useAuth();
  const [tab, setTab] = useState(TAB_REQUESTS);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(0);
  const [rejectingId, setRejectingId] = useState(0);
  const [rejectNote, setRejectNote] = useState('');
  const [toast, setToast] = useState(null);

  const loadRequests = useCallback(async () => {
    setLoadingReqs(true);
    setError(null);
    try {
      const data = await api.get('/api/admin/signup-requests');
      setRequests(Array.isArray(data && data.requests) ? data.requests : []);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoadingReqs(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    setError(null);
    try {
      const data = await api.get('/api/admin/users');
      setUsers(Array.isArray(data && data.users) ? data.users : []);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (!user || !user.is_admin) return;
    loadRequests();
  }, [user, loadRequests]);

  useEffect(() => {
    if (tab === TAB_USERS && user && user.is_admin && users.length === 0) {
      loadUsers();
    }
  }, [tab, user, users.length, loadUsers]);

  function showToast(text) {
    setToast(text);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2600);
  }

  async function approve(id) {
    if (busyId) return;
    setBusyId(id);
    setError(null);
    try {
      const data = await api.post(
        `/api/admin/signup-requests/${id}/approve`,
        {},
      );
      showToast(
        data && data.mail_sent === false
          ? 'Approved — but the welcome email failed to send. Check server logs.'
          : 'Approved. Welcome email sent.',
      );
      await loadRequests();
      // Users list is now stale.
      setUsers([]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(0);
    }
  }

  async function reject(id, note) {
    if (busyId) return;
    setBusyId(id);
    setError(null);
    try {
      await api.post(`/api/admin/signup-requests/${id}/reject`, { note });
      showToast('Rejected.');
      await loadRequests();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(0);
      setRejectingId(0);
      setRejectNote('');
    }
  }

  if (!user) {
    return (
      <Div>
        <div className="adm__empty">
          <p>Sign in to view the admin panel.</p>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </Div>
    );
  }
  if (!user.is_admin) {
    return (
      <Div>
        <div className="adm__empty">
          <p>You must be an admin to view this window.</p>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </Div>
    );
  }

  const pending = requests.filter(r => r.status === 'pending');
  const decided = requests.filter(r => r.status !== 'pending');

  return (
    <Div>
      <header className="adm__toolbar">
        <button
          type="button"
          className={
            'adm__tab' + (tab === TAB_REQUESTS ? ' adm__tab--active' : '')
          }
          onClick={() => setTab(TAB_REQUESTS)}
        >
          Signup Requests
          {pending.length > 0 && (
            <span className="adm__badge">{pending.length}</span>
          )}
        </button>
        <button
          type="button"
          className={
            'adm__tab' + (tab === TAB_USERS ? ' adm__tab--active' : '')
          }
          onClick={() => setTab(TAB_USERS)}
        >
          Users
        </button>
        <div className="adm__spacer" />
        <button
          type="button"
          className="adm__mail-btn"
          title="Open mail@lottel.fun in Hostinger webmail"
          onClick={() =>
            window.open(WEBMAIL_URL, '_blank', 'noopener,noreferrer')
          }
        >
          <img src={mailIcon} alt="" />
          Open Mailbox
        </button>
        <button
          type="button"
          className="adm__refresh"
          onClick={
            tab === TAB_REQUESTS
              ? loadRequests
              : () => {
                  setUsers([]);
                  loadUsers();
                }
          }
        >
          Refresh
        </button>
      </header>

      {error && <div className="adm__error">{error}</div>}
      {toast && <div className="adm__toast">{toast}</div>}

      {tab === TAB_REQUESTS ? (
        <section className="adm__body">
          <h3 className="adm__section-title">
            Pending {pending.length > 0 ? `(${pending.length})` : ''}
          </h3>
          {loadingReqs ? (
            <p className="adm__muted">Loading…</p>
          ) : pending.length === 0 ? (
            <p className="adm__muted">
              No pending requests. When someone applies you&apos;ll see it here
              and at mail@lottel.fun.
            </p>
          ) : (
            <ul className="adm__list">
              {pending.map(r => {
                const busy = busyId === r.id;
                return (
                  <li key={r.id} className="adm__item">
                    <div className="adm__item__head">
                      <img src={userIcon} alt="" className="adm__item__icon" />
                      <div className="adm__item__meta">
                        <div className="adm__item__title">
                          {r.display_name || r.username}{' '}
                          <span className="adm__item__handle">
                            (@{r.username})
                          </span>
                        </div>
                        <div className="adm__item__sub">
                          {r.email} · applied {formatTime(r.created_at)}
                        </div>
                      </div>
                    </div>
                    {r.reason && (
                      <div className="adm__item__reason">{r.reason}</div>
                    )}
                    {rejectingId === r.id ? (
                      <div className="adm__item__actions">
                        <input
                          type="text"
                          value={rejectNote}
                          onChange={e => setRejectNote(e.target.value)}
                          placeholder="Reason (optional — sent to applicant)"
                          maxLength={500}
                          disabled={busy}
                          autoFocus
                        />
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => reject(r.id, rejectNote.trim())}
                        >
                          {busy ? 'Rejecting…' : 'Confirm Reject'}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            setRejectingId(0);
                            setRejectNote('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="adm__item__actions">
                        <button
                          type="button"
                          className="adm__approve"
                          disabled={busy}
                          onClick={() => approve(r.id)}
                        >
                          {busy ? 'Approving…' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          className="adm__reject"
                          disabled={busy}
                          onClick={() => {
                            setRejectingId(r.id);
                            setRejectNote('');
                          }}
                        >
                          Reject…
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {decided.length > 0 && (
            <>
              <h3 className="adm__section-title">Recent decisions</h3>
              <ul className="adm__list adm__list--muted">
                {decided.slice(0, 20).map(r => (
                  <li key={r.id} className="adm__item adm__item--decided">
                    <div className="adm__item__sub">
                      <strong>{r.username}</strong> ({r.email}) —{' '}
                      <span
                        className={
                          r.status === 'approved'
                            ? 'adm__pill adm__pill--ok'
                            : 'adm__pill adm__pill--bad'
                        }
                      >
                        {r.status}
                      </span>{' '}
                      by {r.decided_by || 'admin'} · {formatTime(r.decided_at)}
                      {r.decision_note && ` — ${r.decision_note}`}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      ) : (
        <section className="adm__body">
          {loadingUsers ? (
            <p className="adm__muted">Loading…</p>
          ) : (
            <table className="adm__table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Display name</th>
                  <th>Email</th>
                  <th>Admin</th>
                  <th>Registered</th>
                  <th>Last login</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.username}</td>
                    <td>{u.display_name}</td>
                    <td>{u.email}</td>
                    <td>{u.is_admin ? 'yes' : ''}</td>
                    <td>{formatTime(u.registered)}</td>
                    <td>{formatTime(u.last_login)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </Div>
  );
}

const Div = styled.div`
  background: #ece9d8;
  color: #000;
  font-family: Tahoma, sans-serif;
  font-size: 12px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  .adm__toolbar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    background: linear-gradient(#f4f2e5, #ece9d8);
    border-bottom: 1px solid #a4b0be;
  }
  .adm__tab {
    background: #f0eee0;
    border: 1px solid #a4b0be;
    padding: 3px 10px;
    font: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .adm__tab--active {
    background: #fff;
    font-weight: bold;
  }
  .adm__badge {
    background: #d43a3a;
    color: #fff;
    border-radius: 8px;
    padding: 0 6px;
    font-size: 10px;
    font-weight: bold;
  }
  .adm__spacer {
    flex: 1;
  }
  .adm__mail-btn,
  .adm__refresh {
    background: #f0eee0;
    border: 1px solid #a4b0be;
    padding: 3px 10px;
    font: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .adm__mail-btn img {
    width: 16px;
    height: 16px;
    image-rendering: pixelated;
  }

  .adm__error {
    margin: 6px 8px 0;
    background: #ffe1e1;
    border: 1px solid #d43a3a;
    color: #6a1010;
    padding: 4px 8px;
  }
  .adm__toast {
    margin: 6px 8px 0;
    background: #e2f3d8;
    border: 1px solid #6a9a48;
    color: #244a10;
    padding: 4px 8px;
  }

  .adm__body {
    flex: 1;
    overflow: auto;
    padding: 10px 12px;
  }
  .adm__section-title {
    font-size: 12px;
    margin: 4px 0 8px;
    color: #0b3d91;
  }
  .adm__muted {
    color: #555;
  }
  .adm__empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 30px;
  }

  .adm__list {
    list-style: none;
    padding: 0;
    margin: 0 0 12px;
  }
  .adm__list--muted .adm__item__sub {
    color: #555;
  }
  .adm__item {
    border: 1px solid #c8c5b1;
    background: #fff;
    padding: 8px 10px;
    margin-bottom: 6px;
  }
  .adm__item--decided {
    background: #f6f5ea;
  }
  .adm__item__head {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .adm__item__icon {
    width: 24px;
    height: 24px;
    image-rendering: pixelated;
  }
  .adm__item__title {
    font-weight: bold;
  }
  .adm__item__handle {
    font-weight: normal;
    color: #555;
  }
  .adm__item__sub {
    color: #333;
    margin-top: 2px;
    font-size: 11px;
  }
  .adm__item__reason {
    margin: 6px 0 0;
    padding: 4px 8px;
    background: #f4f2e5;
    border-left: 3px solid #245edc;
    font-style: italic;
    color: #333;
  }
  .adm__item__actions {
    margin-top: 8px;
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .adm__item__actions input {
    flex: 1;
    padding: 3px 5px;
    border: 1px solid #7f9db9;
    font: inherit;
  }
  .adm__item__actions button {
    background: #f0eee0;
    border: 1px solid #a4b0be;
    padding: 3px 10px;
    font: inherit;
    cursor: pointer;
  }
  .adm__approve {
    background: #cfe8bf !important;
  }
  .adm__reject {
    background: #efd0d0 !important;
  }
  .adm__pill {
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: bold;
    text-transform: uppercase;
  }
  .adm__pill--ok {
    background: #cfe8bf;
    color: #1e4a10;
  }
  .adm__pill--bad {
    background: #efd0d0;
    color: #6a1010;
  }

  .adm__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
  }
  .adm__table th,
  .adm__table td {
    padding: 3px 6px;
    text-align: left;
    border-bottom: 1px solid #ddd8c1;
  }
  .adm__table th {
    background: #f0eee0;
    font-weight: bold;
  }
`;
